<template>
    <div class="shift-month-summary">
        <div class="shift-month-summary-head">
            <span class="shift-month-summary-muted">{{ monthName }} · запланировано</span>
            <span>
                <b class="shift-month-summary-total">{{ formatNumber(planHours) }}</b>
                <span class="shift-month-summary-muted">{{ norm !== null ? ` из ${formatNumber(norm)} ч` : ' ч' }}</span>
            </span>
        </div>
        <div class="shift-month-summary-progress" aria-hidden="true">
            <div v-if="progress !== null" class="shift-month-summary-progress-fill" :style="{ width: `${progress}%` }" />
        </div>
        <div class="shift-month-summary-values">
            <span>Основных <b>{{ formatNumber(mainHours) }}</b></span>
            <span>Доп. <b>{{ formatNumber(extraHours) }}</b></span>
            <span>Смен <b>{{ shiftsNumber }}</b></span>
            <span class="shift-month-summary-violations">
                <i v-if="warning > 0" class="shift-month-summary-dot" aria-hidden="true" />Нарушений <b>{{ warning }}</b>
            </span>
            <span>Предупр. <b>{{ moderate }}</b></span>
            <span>Ставка <b>{{ salary === null ? '—' : `${formatNumber(salary)} ₽/ч` }}</b></span>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { Schedule } from '@/types'
import { useScheduleStore } from '@/stores/scheduleStore'
import { useEmployeeStats } from '../employee/useEmployeeStats'

const props = defineProps<{ schedule: Schedule }>()

const store = useScheduleStore()
const { planHours, extraHours, mainHours, moderate, warning, salary, shiftsNumber, norm } = useEmployeeStats(() => props.schedule, { requireCurrentContext: true })

const monthName = computed(() => {
    const name = store.currentScheduleDate.format('MMMM')
    return name.charAt(0).toUpperCase() + name.slice(1)
})
// Известная норма, в т. ч. 0, выводится; процент — только при норме > 0 (без деления на ноль),
// длина ограничена контейнером.
const progress = computed(() => {
    if (norm.value === null || norm.value <= 0 || planHours.value === null) return null
    return Math.min(100, Math.max(0, (planHours.value / (norm.value as number)) * 100))
})

const numberFormat = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 })
const formatNumber = (value: number | null) => value === null ? '—' : numberFormat.format(value)
</script>

<style scoped>
.shift-month-summary {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 10px 12px;
    border: 1px solid var(--color-bg-muted);
    border-radius: 4px;
    font-size: 12px;
    color: var(--color-text-main);
    font-variant-numeric: tabular-nums;
}

.shift-month-summary-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
}

.shift-month-summary-muted {
    color: var(--color-text-muted);
}

.shift-month-summary-total {
    font-size: 13px;
    font-weight: 700;
}

.shift-month-summary-progress {
    height: 4px;
    border-radius: 999px;
    background-color: var(--color-bg-muted);
    overflow: hidden;
}

.shift-month-summary-progress-fill {
    height: 4px;
    background-color: var(--color-brand-main);
}

.shift-month-summary-values {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    color: var(--color-text-muted);
}

.shift-month-summary-values b {
    font-weight: 600;
    color: var(--color-text-main);
}

.shift-month-summary-violations {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.shift-month-summary-dot {
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background-color: var(--color-warning);
}
</style>
